import React, { useEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { displayDate, displayDateTime } from 'reducers/TimeReducer';
import moment from 'moment';
import DateFnsUtils from '@date-io/date-fns';
import RecordLimitAlert from 'Search/components/RecordLimitAlert';
import {
  Fab,
  Tooltip,
  makeStyles,
  withStyles,
  Badge,
  Grid,
  Button,
  TextField,
  Paper,
} from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TextField2 from 'components/TextField2';
import TableContainer from '@material-ui/core/TableContainer';
import ReadOnlyIcon from 'assets/favicon/ReadOnly.png';
import CircularProgress from '@material-ui/core/CircularProgress';
import Checkbox2 from 'components/Checkbox2';
import WarrantPrintsMenu from './WarrantPrintsMenu';
import ReportsDialog from 'components/CADReport/ReportsDialog';
import Dictionary from 'components/Dictionary';
import AddressLookup2 from 'components/AddressLookup2';
import { showAddWarrant, showEditWarrant } from 'reducers/DialogsReducer';
import { getService } from 'reducers/service';
import { searchWarrants } from 'reducers/SearchReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import XGrid3 from 'components/XGrid3';
import clsx from 'clsx';
import DeleteIcon from '@material-ui/icons/Delete';
import NoPermissionRedirect from 'components/NoPermissionRedirect';

const useStyles = makeStyles((theme) => ({
  actions: {
    display: 'flex',
    width: '100%',
    justifyContent: 'right',
    paddingBottom: '14px',
    '& button': {
      marginLeft: 8,
    },
  },
  header: {
    color: theme.palette.colorFont,
  },
  warrantBtn: {
    background: theme.palette.error.main,
    color: 'white',
    '&:hover': {
      background: theme.palette.error.dark,
    },
    '& > span > span > span': {
      background: theme.card.bg,
      border: `1px solid ${theme.palette.error.main}`,
      color: theme.palette.error.main,
    },
  },
  warrant: {
    background: theme.palette.error.main,
    color: theme.card.bg,
    '&.MuiDataGrid-row:hover': {
      background: theme.palette.error.light,
    },
    '&.MuiDataGrid-row.Mui-selected': {
      background: theme.palette.error.dark,
    },
    '&.MuiDataGrid-row.Mui-selected:hover': {
      background: theme.palette.error.dark,
    },
  },
  item2: {
    width: '100%',
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    '& > div': {
      marginRight: theme.spacing(2),
    },
    '& > button': {
      marginTop: 7,
    },
  },
  filterWrap: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  search: {
    width: 450,
    marginRight: 8,
  },
  paper: {
    width: '100%',
    paddintop: '3px',
    boxShadow: 'none !important',
    borderBottom: '1px solid rgba(0, 0, 0, 0.2)'
  },
}));

const ErrorBadge = withStyles({
  badge: {
    boxShadow: '0 0 0 2px #fff',
    '&::after': {
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: '$ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
  button: {
    padding: '0px !important',
    '&:hover': {
      background: 'transparent',
    },
    border: 'none',
    background: 'transparent',
    minWidth: (props) => (props.isEditPage ? '40px' : '64px'),
    marginRight: '-8px',
  },
})(Badge);

const StyledErrorBadge = withStyles({
  badge: {
    backgroundColor: 'var(--danger)',
    color: 'var(--danger)',
    top: '50%', // Move badge to the middle vertically
    left: '8px',
  },
})(ErrorBadge);

const WarrantStyledErrorBadge = withStyles({
  badge: {
    backgroundColor: 'var(--danger)',
    color: 'var(--danger)',
    top: '50%', // Move badge to the middle vertically
    left: '8px',
  },
})(ErrorBadge);

const bool = (params) => (params.value ? <CheckIcon /> : <span></span>);
const renderFullName = (params) => {
  const { row } = params;

  return (
    <WarrantStyledErrorBadge
      overlap="circle"
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      variant={
        (row.DispositionCategory && row.DispositionCategory.toLowerCase() === 'active') ||
        (row.Disposition && row.Disposition.toLowerCase() === 'active')
          ? 'dot'
          : ''
      }>
      <div style={{ paddingLeft: '15px' }}>{row.FullName}</div>
    </WarrantStyledErrorBadge>
  );
};
const warrantColumns = [
  {
    field: 'WarrantID',
    headerName: 'Warrant ID',
    editable: false,
    width: 200,
  },  {
    field: 'FullName',
    headerName: 'FullName',
    width: 200,
    editable: false,
    renderCell: renderFullName,
  },
  {
    field: 'AffidavitText',
    headerName: 'Affidavit Text',
    editable: false,
    width: 200,
  },
  {
    field: 'Address',
    headerName: 'Address',
    editable: false,
    width: 200,
  },
  {
    field: 'IncidentID',
    headerName: 'Incident Number',
    editable: false,
    width: 200,
  },
  {
    field: 'Disposition',
    headerName: 'Disposition',
    editable: false,
    width: 200,
  },

  {
    field: 'BondAmount',
    headerName: 'Bond Amount',
    editable: false,
    width: 200,
  },
  {
    field: 'StatuteCode',
    headerName: 'Statute Code',
    editable: false,
    width: 200,
  },
  {
    field: 'StatuteDescription',
    headerName: 'Statute Description',
    editable: false,
    width: 200,
  },
  {
    field: 'UserWarrantID',
    headerName: 'User Warrant ID',
    editable: false,
    width: 200,
  },
  {
    field: 'WarrantClass',
    headerName: 'Warrant Class',
    editable: false,
    width: 200,
  },
  {
    field: 'Severity',
    headerName: 'Severity',
    editable: false,
    width: 200,
  },
  {
    field: 'Level',
    headerName: 'Level',
    editable: false,
    width: 200,
  },
  {
    field: 'IssuedDate',
    headerName: 'Issued Date',
    valueFormatter: (params) => displayDate(params.value),
    editable: false,
    width: 200,
  },

  {
    field: 'Created',
    headerName: 'Created',
    valueFormatter: (params) => displayDateTime(params.value),
    editable: false,
    width: 200,
  },
  {
    field: 'CreatedBy',
    headerName: 'Created By',
    editable: false,
    width: 200,
  },

  {
    field: 'Updated',
    headerName: 'Updated',
    valueFormatter: (params) => displayDateTime(params.value),
    editable: false,
    width: 200,
  },

  {
    field: 'UpdatedBy',
    headerName: 'Updated By',
    editable: false,
    width: 200,
  },
  {
    field: 'Deleted',
    headerName: 'Deleted',
    editable: false,
    width: 200,
  },
  {
    field: 'DeletedIP',
    headerName: 'Deleted IP',
    editable: false,
    width: 200,
  },
  {
    field: 'IsDeleted',
    headerName: 'IsDeleted',
    width: 200,
    editable: false,
    renderCell: bool,
    format: 'bool',
  },
  {
    field: 'DeletedBy',
    headerName: 'Deleted By',
    editable: false,
    width: 200,
  },
];

function Warrants(props) {
  const classes = useStyles();
  const LIMIT = 1000;

  const [rows, setRows] = useState([]);
  const [reportsData, setReportsData] = useState({});
  const [open, setOpen] = useState(false);
  const [printFlag, setPrintFlag] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(LIMIT);
  const [showAlert, setShowAlert] = useState(false);
  const [spinnerLoading, setSpinnerLoading] = useState(true);
  const [columns, setColumns] = useState(warrantColumns);
  const [selection, setSelection] = useState(null);
  const [warrantFilters, setWarrantFilters] = useState({
    activeOnly: true,
  });
  const dataUpdate = useSelector((state) => state.dataUpdate);
  const dictionary = useSelector((state) => state.dictionary);
  const { codeWarrantClasses, codeWarrantDispositions } = dictionary;
  const throttleRef = useRef(0);
  const throttleRef2 = useRef(0);
  const service = getService('rms-warrant');

  const warrantPermission = getFullPermissions('globals', 'Warrants', 'any'); 
  useEffect(() => {
    search();
    // eslint-disable-next-line
  }, [
    dataUpdate,
    warrantFilters.issuedDateFrom,
    warrantFilters.issuedDateTo,
    warrantFilters.activeOnly,
    warrantFilters.level,
    warrantFilters.severity,
    warrantFilters.dispositionIntCode,
    warrantFilters.warrantClass,
    warrantFilters.userWarrantID,
    warrantFilters.address,
    pageSize,
  ]);

  useEffect(() => {
    clearTimeout(throttleRef.current);
    throttleRef.current = setTimeout(() => {
      search();
    }, 500);
    // eslint-disable-next-line
  }, [warrantFilters.text]);
  useEffect(() => {
    clearTimeout(throttleRef2.current);
    throttleRef2.current = setTimeout(() => {
      search();
    }, 500);
    // eslint-disable-next-line
  }, [warrantFilters.incidentNumber]);
  useEffect(() => {
    if (rows.length === rowCount) setShowAlert(false);
    else if (rows.length >= pageSize) setShowAlert(true);
    // eslint-disable-next-line
  }, [rows, rowCount]);

  const handleShowAll = () => {
    if (rowCount > pageSize) {
      setPageSize(rowCount);
      search(page);
    }
  };

  const search = async (page = 0) => {
    setSpinnerLoading(true);
    searchWarrants(warrantFilters, page, pageSize)
      .then((result) => {
        result.length ? setRowCount(result[0].Count) : setRowCount(0);
        setRows(processData(result));
      })
      .catch((err) => console.log(err))
      .finally(() => setSpinnerLoading(false));
  };
  const processData = (data) => {
    return data
      .filter(row => row.WarrantID != null)
      .map(row => ({
        ...row,
        id: row.WarrantID,
      }));
  };

  const onPageChange = () => {
    if (pageSize <= rowCount) {
      setPageSize(pageSize + LIMIT);
      search(page);
    }
  };

  const handleFilterChange = (filedName, value) => {
    let newForm = { ...warrantFilters };
    newForm[filedName] = value;
    setWarrantFilters(newForm);
  };

  const handleAdd = () => {
    props.showAddWarrant();
  };

  const handleEdit = () => {
    if (!selection) return;
    props.showEditWarrant(selection);
  };

  const handleDelete = async () => {
    if (!selection) return;
    const { ptsWarrantID } = selection;
    try {
      await service
        .patch(ptsWarrantID, {
          type: 'remove_warrant',
          data: {
            ptsWarrantID: ptsWarrantID,
          },
        })
        .then((res) => {
          search();
        });
    } catch (e) {
      console.log(e);
    }
  };

  const handleDuplicateWarrant = async () => {
    if (!window.confirm('Are you sure you want to duplicate selected warrant?')) return;
    if (!selection) return;

    await service.create({ type: 'duplicate_warrant', data: selection });
    search();
  };

  const getWarrantDetailList = () => {
    // StartDate calculation
    let startDateTime = moment(warrantFilters?.issuedDateFrom).format('MM-DD-YYYY').toString();

    // EndDate calculation
    let endDateTime = moment(warrantFilters?.issuedDateTo).format('MM-DD-YYYY').toString();

    setOpen(true);
    setReportsData({
      id: 264,
      data: {
        StartDate: startDateTime,
        EndDate: endDateTime,
      },
    });
  };

  const generateReport = (type, id) => {
    if (!selection) return;
    const { ptsWarrantID } = selection;

    switch (type) {
      case 'warrantdetaillist':
        getWarrantDetailList(id);
        break;
      case 'list':
        setPrintFlag(true);
        setTimeout(() => setPrintFlag(false), 1000);
        break;
      default:
        break;
    }
    if (!['list', 'warrantdetaillist'].includes(type)) {
      setReportsData({ id, data: { ptsWarrantID } });
      setOpen(true);
    }
  };

  const closeReport = (id, arr) => {
    setOpen(false);
  };
  if (!warrantPermission.Read) return <NoPermissionRedirect />;
  const renderActions = () => (
    <Grid item md={3} className={classes.actions}>
      <WarrantPrintsMenu
        tooltipText="Print Warrant"
        size="20px"
        generateReport={(type, id) => generateReport(type, id)}
        selection={selection}
        type="add"
        startDate={warrantFilters?.issuedDateFrom}
        endDate={warrantFilters?.issuedDateTo}
      />
      <Tooltip title="Add Warrant">
        <Fab
          size="small"
          disabled={!warrantPermission.Create}
          color="secondary"
          onClick={handleAdd}>
          <AddIcon />
        </Fab>
      </Tooltip>
      {selection !== null && (
        <>
          <Tooltip title="Duplicate warrant">
            <Fab size="small" color="secondary" onClick={handleDuplicateWarrant}>
              <ContentCopyIcon />
            </Fab>
          </Tooltip>
          <Tooltip title={warrantPermission.Edit ? 'Edit warrant' : 'View Warrant'}>
            <Fab size="small" color="secondary" onClick={handleEdit}>
              {warrantPermission.Edit ? <EditIcon /> : <img src={ReadOnlyIcon} width={25} />}
            </Fab>
          </Tooltip>
          <Tooltip title="Delete Warrant">
            <Fab
              size="small"
              disabled={!warrantPermission.Delete}
              color="secondary"
              onClick={handleDelete}>
              <DeleteIcon />
            </Fab>
          </Tooltip>
        </>
      )}
    </Grid>
  );
  const handleAddressSelection = (newVal) => {
    handleFilterChange('address', newVal);
  };
  const renderDispositionCode = () => {
    const handleChange = (e, value) => {
      handleFilterChange('dispositionIntCode', value);
      if (value) {
        setWarrantFilters((prev) => ({
          ...prev,
          activeOnly: false,
        }));
      }
    };
    return (
      <Dictionary
        compact
        options="codeWarrantDispositions"
        className="mt-2"
        onChange={handleChange}
        label="Disposition"
        value={warrantFilters.dispositionIntCode}
      />
    );
  };
  const renderWarrantClass = () => {
    const handleChange = (e, value) => {
      handleFilterChange('warrantClass', value);
    };
    return (
      <Dictionary
        compact
        options="codeWarrantClasses"
        className="mt-2"
        onChange={handleChange}
        value={warrantFilters.warrantClass}
        label="Warrant Class"
      />
    );
  };


  return (
    <Grid container className={classes.root}>
      <Grid item xs={12}>
        <div className={clsx(classes.root)}>
          <Paper style={{ minHeight: window.innerHeight - 430 + 'px' }} className={classes.paper}>
            <h5 className={classes.header}>Warrants</h5>
            <Grid container justify="space-between" alignItems="center">
              <Grid item md={9} container spacing={1} alignItems="center" className="mb-2">
                <Grid item xs={2}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                    //   disableOpenOnEnter
                      autoComplete="off"
                      // disableToolbar
                      variant="inline"
                      inputVariant="outlined"
                      format="MM/dd/yyyy"
                      label="Issue From Date"
                      value={warrantFilters.issuedDateFrom || null}
                      onChange={(date) => handleFilterChange('issuedDateFrom', date)}
                      autoOk
                      size="small"
                      className={classes.item2}
                    />
                  </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={2}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                      autoComplete="off"
                      // disableToolbar
                      variant="inline"
                      inputVariant="outlined"
                      format="MM/dd/yyyy"
                      label="Issue To Date"
                      value={warrantFilters.issuedDateTo || null}
                      onChange={(date) => handleFilterChange('issuedDateTo', date)}
                      autoOk
                      size="small"
                      className={classes.item2}
                    />
                  </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={2}>
                  <TextField2
                    value={warrantFilters.incidentNumber || ''}
                    onChange={(e) => handleFilterChange('incidentNumber', e.target.value)}
                    type="search"
                    className={classes.item2}
                    compact="true"
                    placeholder="Incident Number"
                    label="Incident Number"
                  />
                </Grid>
                <Grid item xs={2}>
                  <Dictionary
                    options="codeStatuteSeverity"
                    value={warrantFilters.severity || []}
                    label="Severity"
                    className="mt-2"
                    compact
                    onChange={(ev, newVal) => {
                      handleFilterChange('severity', newVal);
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Dictionary
                    options="codeStatuteLevel"
                    value={warrantFilters.level || ''}
                    label="Level"
                    className="mt-2"
                    compact
                    onChange={(ev, newVal) => {
                      handleFilterChange('level', newVal);
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  {renderDispositionCode()}
                </Grid>
                <Grid item xs={2}>
                  {renderWarrantClass()}
                </Grid>
                <Grid item xs={2}>
                  <TextField2
                    value={warrantFilters.userWarrantID || ''}
                    onChange={(e) => handleFilterChange('userWarrantID', e.target.value)}
                    type="search"
                    className={classes.item2}
                    compact="true"
                    placeholder="User Warrant ID"
                    label="User Warrant ID"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField2
                    value={warrantFilters.text || ''}
                    onChange={(e) => handleFilterChange('text', e.target.value)}
                    type="search"
                    className={classes.item2}
                    compact="true"
                    placeholder="Name or Warrant ID"
                    label="Quick Filter"
                  />
                </Grid>
                <Grid item xs={3}>
                  <AddressLookup2
                    className={classes.w100pr}
                    onAddressValueSet={handleAddressSelection}
                    onPlaceValueSet={handleAddressSelection}
                    defaultLocation={null}
                    onReset={() => handleAddressSelection(null)}
                    // setPlace={filterFormAddress?.address}
                    compact="true"
                    label="Search Address"
                  />
                </Grid>
                <Grid item xs={2}>
                  <Checkbox2
                    label="Active Warrants Only"
                    size="sm"
                    className="mt-2"
                    selectedCheckbox={warrantFilters.activeOnly}
                    onChange={(ev) => handleFilterChange('activeOnly', ev.target.checked)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    // className={classes.item2}
                    // color=''
                    // variant="outlined"
                    style={{ border: '1px solid #ccc' }}
                    onClick={() => setWarrantFilters({})}>
                    Clear filters
                  </Button>
                </Grid>
              </Grid>

              {renderActions()}
            </Grid>
            <TableContainer
              style={{
                overflowX: 'hidden',
                maxHeight: window.innerHeight - 260 + 'px',
                marginBottom: '20px',
                // marginTop: '20px',
              }}>
              <div
                style={{
                  height: window.innerHeight - 340 + 'px',
                  overflowY: 'hidden',
                }}>
                {spinnerLoading && (
                  <div className={classes.spinnerLoading} style={{ margin: '300px 950px' }}>
                    <CircularProgress disableShrink />
                  </div>
                )} {!spinnerLoading && (
                    <>
                      {showAlert && (
                        <RecordLimitAlert
                          handleShowAll={handleShowAll}
                          handleNextPage={onPageChange}
                          rowCount={rowCount}
                          currentRows={rows.length}
                        />
                      )}
                      <XGrid3 
                        name={"Warrant"}
                        rows={rows}
                        setSelection={(selectedId) => {
                          const selectedRow = rows.find(r => r.id === selectedId);
                          setSelection(selectedRow || null);
                        }}
                        printFlag={printFlag}
                        columns={columns}
                        setColumns={setColumns}
                        refresh={search}
                      /> 
                    </>
                  )}
              </div>
            </TableContainer>
            {open && <ReportsDialog open={open} onClose={closeReport} report={reportsData} />}
          </Paper>
        </div>
      </Grid>
    </Grid>
  );
  
}

export default connect(null, { showAddWarrant, showEditWarrant })(Warrants);
